import { ComboBox, IDropdownOption, Spinner } from '@fluentui/react';
import { ColumnDto, MicrosoftEntraIdIntegration } from '../../../../../../../api/generated/data-contracts';
import { Dispatch, SetStateAction, useRef } from 'react';
import { DATAVERSE_ENVIRONMENTS_TABLES, DATAVERSE_RESOURCE_COLUMNS, DESELECT_KEY } from '../../../../../../../constants/queryKeys';
import produce from 'immer';
import { useQuery } from '@tanstack/react-query';
import { ApiCalls } from '../../../../../../../api/api';
import { onRenderOption } from '../../../components/ComboBoxTooltip';
import { EnvironmentDropdown } from '../../Dataverse/EnvironmentDropdown';

interface IDataverseResourcesPaneProps {
    integration: MicrosoftEntraIdIntegration;
    setIntegration: Dispatch<SetStateAction<MicrosoftEntraIdIntegration>>;
}

export const DataverseResourcesPane = ({ integration, setIntegration }: IDataverseResourcesPaneProps) => {
    const {
        dataverseUrl,
        dataverseResourceTable,
        dataverseResourceId,
        dataverseResourceAzureId,
        dataverseResourcePrincipalName,
        dataverseResourceEmail,
        dataverseResourceName,
    } = integration.configuration;

    const defaultDataverseTables = useRef([] as IDropdownOption<ColumnDto>[]).current;
    const { data: dataverseTables = defaultDataverseTables } = useQuery({
        queryKey: [DATAVERSE_ENVIRONMENTS_TABLES, dataverseUrl],
        queryFn: () =>
            ApiCalls.dataverseEnvironmentsTablesList({ id: integration.id, environmentUrl: dataverseUrl }).then(res =>
                res.data.map(table => ({ key: table.logicalName, text: table.displayName || table.logicalName, data: table } as IDropdownOption<any>)),
            ),
        enabled: Boolean(dataverseUrl),
    });

    const defaultColumns = useRef([] as IDropdownOption<ColumnDto>[]).current;
    const { data: resourceColumns = defaultColumns } = useQuery({
        queryKey: [DATAVERSE_RESOURCE_COLUMNS, dataverseUrl, dataverseResourceTable],
        queryFn: () =>
            ApiCalls.dataverseEnvironmentsTablesColumnsList({
                environmentUrl: dataverseUrl,
                tableName: dataverseResourceTable,
            }).then(
                res =>
                    [
                        { key: DESELECT_KEY, text: 'Not selected', data: { logicalName: '' } },
                        ...res.data.map(
                            column => ({ key: column.logicalName, text: column.displayName || column.logicalName, data: column } as IDropdownOption<ColumnDto>),
                        ),
                    ] as IDropdownOption<ColumnDto>[],
            ),
        enabled: Boolean(dataverseUrl && dataverseResourceTable),
        suspense: true,
    });

    return (
        <>
            <EnvironmentDropdown
                defaultSelectedKey={dataverseUrl}
                onChange={(e, option) => {
                    setIntegration(
                        produce(integration => {
                            integration.configuration.dataverseUrl = option.text || '';
                        }),
                    );
                }}
                disabled={false}
            />
            <ComboBox
                label="Dataverse Resource Table"
                allowFreeform
                autoComplete="on"
                options={dataverseTables}
                onChange={(e, opt, index, value) => {
                    if (opt) {
                        setIntegration(
                            produce(integration => {
                                integration.configuration.dataverseResourceTable = opt.key as string;
                                // integration.configuration.dataverseResourceTable = opt.data.logicalName;
                            }),
                        );
                    } else if (!value) {
                        setIntegration(
                            produce(integration => {
                                integration.configuration.dataverseResourceTable = '';
                            }),
                        );
                    }
                }}
                selectedKey={dataverseResourceTable ?? ''}
                onRenderOption={onRenderOption}
                disabled={dataverseTables.length <= 0}
            />
            {dataverseResourceTable && !resourceColumns && <Spinner />}
            <ComboBox
                label="Id"
                allowFreeform
                autoComplete="on"
                required
                options={resourceColumns}
                onChange={(e, opt, index, value) => {
                    setIntegration(
                        produce(integration => {
                            integration.configuration.dataverseResourceId = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={resourceColumns.length <= 0}
                selectedKey={dataverseResourceId ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Name"
                allowFreeform
                autoComplete="on"
                required
                options={resourceColumns}
                onChange={(e, opt, index, value) => {
                    setIntegration(
                        produce(integration => {
                            integration.configuration.dataverseResourceName = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={resourceColumns.length <= 0}
                selectedKey={dataverseResourceName ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Email"
                allowFreeform
                autoComplete="on"
                required
                options={resourceColumns}
                onChange={(e, opt, index, value) => {
                    setIntegration(
                        produce(integration => {
                            integration.configuration.dataverseResourceEmail = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={resourceColumns.length <= 0}
                selectedKey={dataverseResourceEmail ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Principal Name"
                allowFreeform
                autoComplete="on"
                required
                options={resourceColumns}
                onChange={(e, opt, index, value) => {
                    setIntegration(
                        produce(integration => {
                            integration.configuration.dataverseResourcePrincipalName = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={resourceColumns.length <= 0}
                selectedKey={dataverseResourcePrincipalName ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Azure Id"
                allowFreeform
                autoComplete="on"
                required
                options={resourceColumns}
                onChange={(e, opt, index, value) => {
                    setIntegration(
                        produce(integration => {
                            integration.configuration.dataverseResourceAzureId = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={resourceColumns.length <= 0}
                selectedKey={dataverseResourceAzureId ?? ''}
                onRenderOption={onRenderOption}
            />
        </>
    );
};
